

import React, { useState } from 'react';
import SendIcon from './SendIcon';
import AnimatedBackground from './AnimatedBackground'; 

interface LandingPageProps {
  onNavigateToGenerate: () => void;
  onNavigateToMyCreations: () => void; // Relies on username via App's state
  onNavigateToMyExports: () => void; // Relies on username via App's state
  onNavigateToMainCategories: () => void;
  onNavigateToManageCustomEdits: () => void;
  onNavigateToVideoFromImage: () => void;
  onNavigateToVideoWithAudio: () => void;
}

interface ShortcutCardProps {
  title: string;
  description: string;
  onClick: () => void;
  icon?: React.ReactNode;
  bgColorClassName?: string; 
}

const ShortcutCard: React.FC<ShortcutCardProps> = ({ title, description, onClick, icon, bgColorClassName }) => (
  <button
    onClick={onClick}
    className={`${bgColorClassName || 'bg-gray-700 hover:bg-gray-600'} p-6 rounded-lg shadow-[4px_4px_16px_rgba(234,179,8,0.5)] transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-75 w-full text-left`}
    aria-label={title}
  >
    <div className="flex items-center mb-2">
      {icon && <span className="mr-3 text-indigo-400">{icon}</span>}
      <h3 className="text-xl font-semibold text-gray-100">{title}</h3>
    </div>
    <p className="text-gray-300 text-sm">{description}</p>
  </button>
);

const LandingPage: React.FC<LandingPageProps> = ({
  onNavigateToGenerate,
  onNavigateToMyCreations,
  onNavigateToMyExports,
  onNavigateToMainCategories,
  onNavigateToManageCustomEdits,
  onNavigateToVideoFromImage,
  onNavigateToVideoWithAudio,
}) => {
  const [userGoal, setUserGoal] = useState('');

  const handleGoalSubmit = () => {
    if (!userGoal.trim()) return;

    const goal = userGoal.toLowerCase();

    if (goal.includes("generar") || goal.includes("crear nueva imagen") || goal.includes("nueva imagen")) {
      onNavigateToGenerate();
    } else if (goal.includes("mis creaciones") || goal.includes("mis imagenes generadas") || goal.includes("trabajo ia") || goal.includes("creaciones ai")) {
      onNavigateToMyCreations();
    } else if (goal.includes("exportadas") || goal.includes("finales") || goal.includes("mi arte") || goal.includes("arteai final")) {
      onNavigateToMyExports();
    } else if (goal.includes("editar acciones") || goal.includes("personalizar acciones") || goal.includes("gestionar edits") || goal.includes("mis edits")) {
      onNavigateToManageCustomEdits();
    } else if (goal.includes("explorar") || goal.includes("galeria") || goal.includes("categorias") || goal.includes("ver imagenes")) {
      onNavigateToMainCategories();
    } else if (goal.includes("video desde imagen")) {
      onNavigateToVideoFromImage();
    } else if (goal.includes("video con audio")) {
      onNavigateToVideoWithAudio();
    }
     else {
      onNavigateToMainCategories();
    }
  };

  const handleTextareaKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault(); 
      handleGoalSubmit();
    }
  };

  const GenerateIcon = () => <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456Z" /></svg>;
  const FolderIcon = () => <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 0 1 4.5 9.75h15A2.25 2.25 0 0 1 21.75 12v.75m-8.69-6.44-2.12-2.12a1.5 1.5 0 0 0-1.061-.44H4.5A2.25 2.25 0 0 0 2.25 6v12a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9a2.25 2.25 0 0 0-2.25-2.25h-5.379a1.5 1.5 0 0 1-1.06-.44Z" /></svg>;
  const ExportIcon = () => <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" d="M9 8.25H7.5a2.25 2.25 0 0 0-2.25 2.25v9a2.25 2.25 0 0 0 2.25 2.25h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25H15m0-3-3-3m0 0-3 3m3-3v12" /></svg>;
  const CategoryIcon = () => <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25A2.25 2.25 0 0 1 13.5 8.25V6ZM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-2.25A2.25 2.25 0 0 1 13.5 18v-2.25Z" /></svg>;
  const SettingsIcon = () => <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" d="M10.343 3.94c.09-.542.56-1.007 1.057-1.007h1.2c.498 0 .967.465 1.057 1.007L13.8 7.487l.277-.15c.418-.226.9-.226 1.318 0l.277.15L17.824 5.6a.75.75 0 0 1 .976-.218l1.245.717a.75.75 0 0 1 .218.976l-1.342 2.324-.149.277c.226.418.226.9 0 1.318l.149.277 1.342 2.324a.75.75 0 0 1-.218.976l-1.245-.717a.75.75 0 0 1-.976-.218l-2.146-1.243-.277.15c-.418.226-.9.226-1.318 0l-.277-.15-2.146 1.243a.75.75 0 0 1-.976.218l-1.245-.717a.75.75 0 0 1-.218-.976l1.342-2.324.149-.277c-.226-.418-.226.9 0-1.318l-.149-.277L3.782 5.6a.75.75 0 0 1 .218-.976L5.246 3.906a.75.75 0 0 1 .976.218l2.146 1.243.277-.15c.418-.227.9-.227 1.318 0l.277.15L10.343 3.94ZM12 15.75a3.75 3.75 0 1 0 0-7.5 3.75 3.75 0 0 0 0 7.5Z" /></svg>;
  const VideoFromImageIcon = () => <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9A2.25 2.25 0 0 0 4.5 18.75Z" /></svg>;
  const VideoWithAudioIcon = () => <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" d="M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 0 0 0 9h.75c.704 0 1.402-.03 2.09-.09m0-9.15c.688.06 1.386.09 2.09.09h.75a4.5 4.5 0 0 1 0 9h-.75c-.704 0-1.402.03-2.09.09m0-9.15c.955 0 1.858.135 2.706.371m-2.706-.371c-.955 0-1.858.135-2.706.371m5.412 0c.848.236 1.64.568 2.366.962M7.926 6.371c.848.236 1.64.568 2.366.962m0-2.75C10.955 3.448 9.755 3 8.25 3c-1.505 0-2.705.448-3.426.962m3.426 0V1.5M5.824 5.607c.928-.322 1.96-.528 3.046-.624m3.046.624c1.086.096 2.118.302 3.046.624m-3.046-.624V1.5m2.25 15.336C12.095 17.552 10.795 18 9 18c-1.795 0-3.095-.448-3.75-1.164M14.25 16.836C13.905 17.552 12.605 18 10.5 18c-2.105 0-3.405-.448-3.75-1.164m11.804-1.202c.34.115.671.244.986.382M5.64 15.634c.315.138.646.267.986.382m0 0a6.723 6.723 0 0 1 7.928 0M6.626 16.016C6 16.732 6 17.648 6 18.75c0 .705 0 1.61.626 2.326m8.748-4.652c.626-.716.626-1.62.626-2.326 0-1.102 0-2.018-.626-2.326M9 15v4.5M15 15v4.5" /></svg>;

  const userSpecificBgColor = "bg-purple-700 hover:bg-purple-600";

  return (
    <div className="relative flex-1 flex flex-col items-center justify-center p-4 sm:p-8 bg-gray-850 text-gray-100 overflow-hidden">
      <AnimatedBackground />
      
      <div className="relative z-10 w-full max-w-3xl text-center">
        <h1 className="text-4xl sm:text-5xl font-bold mb-6 text-gradient-animated-vertical-pastel">
          ¿Qué quieres hacer hoy?
        </h1>
        
        <div className="relative mb-6">
          <textarea
            value={userGoal}
            onChange={(e) => setUserGoal(e.target.value)}
            onKeyDown={handleTextareaKeyDown}
            placeholder="Describe tu objetivo o simplemente elige una opción de abajo..."
            className="w-full p-3 sm:p-4 pr-12 sm:pr-14 rounded-lg bg-gray-700 border-red-500 border-[3px] focus:ring-2 focus:ring-indigo-500 text-gray-100 text-base sm:text-lg resize-none shadow-md"
            rows={3}
            aria-label="Describe tu objetivo"
          />
          <button
            type="button"
            onClick={handleGoalSubmit}
            disabled={!userGoal.trim()}
            className="absolute right-2 bottom-2 p-2 sm:p-2.5 rounded-md text-gray-300 hover:text-indigo-300 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-400"
            aria-label="Enviar objetivo"
          >
            <SendIcon className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>

        <button
          onClick={onNavigateToMainCategories}
          className="w-full max-w-md mx-auto my-6 px-6 py-3 bg-teal-500 hover:bg-teal-600 text-white font-semibold rounded-lg shadow-md transition-colors duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-teal-400 focus:ring-offset-2 focus:ring-offset-gray-800 border-blue-500 border-[5px]"
          aria-label="Ir a la galería principal de imágenes públicas"
        >
          Ir a la Galería
        </button>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mt-2">
          <ShortcutCard
            title="Explorar Mis Creaciones (AI)"
            description="Ve y edita las imágenes que has generado o modificado."
            onClick={onNavigateToMyCreations}
            icon={<FolderIcon />}
            bgColorClassName={userSpecificBgColor}
          />
          <ShortcutCard
            title="Explorar Mi ArteAI Final"
            description="Accede a tus imágenes finales exportadas."
            onClick={onNavigateToMyExports}
            icon={<ExportIcon />}
            bgColorClassName={userSpecificBgColor}
          />
          <ShortcutCard
            title="Generar Nueva Imagen"
            description="Crea una imagen desde cero usando un prompt de texto."
            onClick={onNavigateToGenerate}
            icon={<GenerateIcon />}
          />
          <ShortcutCard
            title="Explorar Categorías Públicas"
            description="Navega por las colecciones de imágenes existentes."
            onClick={onNavigateToMainCategories}
            icon={<CategoryIcon />}
          />
          <ShortcutCard
            title="Gestionar Ediciones Personalizadas"
            description="Crea y administra tus propios atajos de edición rápida."
            onClick={onNavigateToManageCustomEdits}
            icon={<SettingsIcon />}
          />
          <ShortcutCard
            title="Generar Video desde Una Imagen"
            description="Anima una imagen existente para crear un video corto."
            onClick={onNavigateToVideoFromImage}
            icon={<VideoFromImageIcon />}
          />
          <ShortcutCard
            title="Generar Video con Audio"
            description="Crea un video a partir de un prompt y añade audio con Veo3 de Google."
            onClick={onNavigateToVideoWithAudio}
            icon={<VideoWithAudioIcon />}
          />
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
